import React, { Suspense, lazy } from 'react';
import { canLoadPDFJS } from '../../utils/pdfFeatureDetection';
import FullPdfViewerFallback from './FullPDFViewerFallback';

interface FullPdfViewerProps {
  pdfUrl: string;
}

// Lazy load the actual FullPDFViewer component only if the browser supports it
// The import function checks feature support before attempting the import
const FullPdfViewerActual = canLoadPDFJS()
  ? lazy(() => {
      // Double-check feature support before importing (defensive)
      if (!canLoadPDFJS()) {
        // Return a rejected promise to prevent import
        return Promise.reject(new Error('Browser does not support required JavaScript features'));
      }
      return import('./FullPDFViewer').then(module => ({ default: module.FullPdfViewer }));
    })
  : null;

/**
 * Lazy-loaded wrapper for FullPDFViewer that checks browser compatibility
 * before attempting to load pdfjs-dist. Falls back to a simple UI
 * for browsers that cannot parse modern JavaScript.
 */
export const FullPdfViewerLazy: React.FC<FullPdfViewerProps> = (props) => {
  // If browser doesn't support modern JS, show fallback immediately
  if (!canLoadPDFJS() || !FullPdfViewerActual) {
    return <FullPdfViewerFallback {...props} />;
  }

  // Otherwise, lazy load the actual component
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-[400px] border border-gray-300 rounded-lg bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Loading PDF viewer...</p>
          </div>
        </div>
      }
    >
      <FullPdfViewerActual {...props} />
    </Suspense>
  );
};

export default FullPdfViewerLazy;

